import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiCheckCircle, FiLock, FiAlertTriangle, FiUsers, FiDollarSign } = FiIcons;

const CyberInsurance = () => {
  const coverageAreas = [
    { title: 'Data Breaches', description: 'If customer or employee information (like names, addresses, or credit card numbers) is stolen, the policy can help cover the costs of notifying those affected, offering credit monitoring, and legal expenses.', icon: FiLock },
    { title: 'Cyberattacks', description: 'If a hacker breaks into your systems and causes damage, the policy might cover the cost of restoring your data and systems and any loss of income if your business is interrupted.', icon: FiAlertTriangle },
    { title: 'Ransomware', description: 'If a hacker locks your data and demands a ransom, this policy might cover the ransom payment and the cost of getting your data back.', icon: FiShield }
  ];

  const reasons = [
    { title: 'Increasing Cyber Threats', description: 'Cyberattacks are becoming more common, and hackers often see small businesses as easy targets. A cyber insurance policy provides you with a safety net if your business falls victim to an attack.' },
    { title: 'Sensitive Data Protection', description: 'If your business handles sensitive customer or employee information (like credit card details or personal data), a data breach could be costly. Cyber insurance helps cover the expenses related to notifying affected individuals, offering credit monitoring, and managing legal liabilities.' },
    { title: 'Cost of Recovery', description: 'Recovering from a cyber incident can be expensive. This includes costs like restoring data, repairing systems, and managing business interruptions. Cyber insurance helps cover these costs, reducing the financial burden on your business.' },
    { title: 'Compliance and Legal Requirements', description: 'Some industries have strict regulations about data protection. If your business fails to meet these standards, you could face fines and legal action. Cyber coverage can help cover these costs and ensure you\'re meeting compliance requirements.' },
    { title: 'Reputation Management', description: 'A cyber incident can harm your business\'s reputation. Cyber insurance often includes coverage for public relations efforts to help restore trust with customers and the public.' },
    { title: 'Peace of Mind', description: 'Knowing that your business is protected against cyber threats allows you to focus on your core operations without constantly worrying about the potential financial impact of a cyberattack.' },
    { title: 'Vendor and Partner Requirements', description: 'Some vendors, partners, or clients may require you to have cyber insurance as part of their risk management strategies. Having a policy in place can help you maintain and build these business relationships.' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1563206767-5b18f218e8de?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80)',
          }}
        />
        <div className="absolute inset-0 bg-purple-900 bg-opacity-80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              Cyber Insurance for Small Businesses
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto"
            >
              Cyber insurance helps small businesses recover from cyber threats, making it easier to bounce back and continue operations after data breaches, ransomware, and cyberattacks.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex justify-center"
            >
              <a
                href="https://coterie.com/quote"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors shadow-xl"
              >
                Get My Price
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-20 bg-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-100 border-l-4 border-red-500 p-6 rounded-lg text-center">
            <div className="flex items-center justify-center mb-4">
              <SafeIcon icon={FiAlertTriangle} className="h-8 w-8 text-red-600 mr-4" />
              <h3 className="text-2xl font-semibold text-red-800">
                Critical Cyber Security Statistic
              </h3>
            </div>
            <p className="text-xl text-red-700">
              <strong>88% of small business owners</strong> have felt their business was vulnerable to a cyber attack.
            </p>
          </div>
        </div>
      </section>

      {/* Coverage Areas Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Does Cyber Insurance Cover?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A cyber insurance policy typically covers the most common and costly cyber threats facing small businesses today.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {coverageAreas.map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-xl shadow-lg"
              >
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={area.icon} className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">{area.title}</h3>
                <p className="text-gray-600 text-center">{area.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why You Need It Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Your Small Business Needs Cyber Insurance
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Here are key reasons you should consider buying cyber coverage as a small business owner:
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {reasons.map((reason, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 p-6 rounded-xl"
              >
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-purple-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{reason.title}</h3>
                    <p className="text-gray-600">{reason.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Summary */}
      <section className="py-20 bg-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Comprehensive Cyber Protection
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Cyber coverage provides financial protection, peace of mind, and can even be a competitive advantage in today's digital landscape. Don't wait for the unthinkable to happen.
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiDollarSign} className="h-6 w-6 text-purple-500" />
                  <span className="text-gray-800">Monthly or annual payment options</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiUsers} className="h-6 w-6 text-purple-500" />
                  <span className="text-gray-800">Seamless and stress-free process</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiShield} className="h-6 w-6 text-purple-500" />
                  <span className="text-gray-800">Comprehensive threat coverage</span>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white p-8 rounded-xl shadow-xl"
            >
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Protected Today</h3>
                <p className="text-gray-600 mb-6">
                  Don't leave your business vulnerable to cyber threats. Get comprehensive cyber insurance coverage through Coterie Insurance.
                </p>
                <a
                  href="https://coterie.com/quote"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-purple-700 transition-colors shadow-lg mb-4"
                >
                  Get My Price
                </a>
                <p className="text-sm text-gray-500">
                  Instant quotes • Competitive rates • Expert support
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CyberInsurance;